/**
 * EventPady+ API Endpoint Registry
 *
 * All API paths in one place. Never hardcode paths in components or hooks.
 * Versioning is handled by the Axios base URL (configured in client.ts).
 */
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN:           'auth/login/',
    REFRESH:         'auth/refresh/',
    LOGOUT:          'auth/logout/',
    ME:              'auth/me/',
    PROFILE_PHOTO:   'auth/me/photo/',
    CHANGE_PASSWORD: 'auth/password/change/',
    FORGOT_PASSWORD: 'auth/password/reset/',
    RESET_PASSWORD:  'auth/password/reset/confirm/',
    SIGNUP:          'auth/register/',
    INVITATION:        (token: string) => `auth/invitations/${token}/`,
    INVITATION_ACCEPT: (token: string) => `auth/invitations/${token}/accept/`,
    ONBOARDING: {
      PROGRESS:      'onboarding/progress/',
      COMPLETE_STEP: 'onboarding/complete-step/',
      DISMISS:       'onboarding/dismiss/',
    },
  },
  ORGANIZATIONS: {
    DETAIL:        'organizations/me/',
    SETTINGS:      'organizations/me/settings/',
    // Dynamic field config (V1.5) — org-level field activation management
    FIELD_CONFIG:  'organizations/me/field-config/',
    MEMBERS:       'organizations/me/members/',
    MEMBER_DETAIL: (id: string) => `organizations/me/members/${id}/`,
    INVITE:        'organizations/me/members/invite/',
  },
  VISITORS: {
    LIST:               'visitors/',
    CREATE:             'visitors/',
    DETAIL:             (id: string) => `visitors/${id}/`,
    UPDATE:             (id: string) => `visitors/${id}/`,
    DELETE:             (id: string) => `visitors/${id}/`,
    FLAG:               (id: string) => `visitors/${id}/flag/`,
    UNFLAG:             (id: string) => `visitors/${id}/unflag/`,
    VISITS:             (id: string) => `visitors/${id}/visits/`,
    LOG_VISIT:          (id: string) => `visitors/${id}/log-visit/`,
    SMS_HISTORY:        (id: string) => `visitors/${id}/sms-messages/`,
    EXPORT:             'visitors/export/',
    CHECK_PHONE:        'visitors/check-phone/',
    PURPOSES:           'visitor-purposes/',
    PURPOSE_DETAIL:     (id: string) => `visitor-purposes/${id}/`,
    PURPOSE_ACTIVATE:   (id: string) => `visitor-purposes/${id}/activate/`,
    PURPOSE_DEACTIVATE: (id: string) => `visitor-purposes/${id}/deactivate/`,
    // Workflow config (V1.1)
    WORKFLOW_DEFAULTS:  'visitor-purposes/workflow-defaults/',
    WORKFLOW_CONFIG:    (id: string) => `visitor-purposes/${id}/workflow-config/`,
    // Photo capture (V1.2)
    PHOTO:              (id: string) => `visitors/${id}/photo/`,
    SEND_SMS:           (id: string) => `visitors/${id}/send-sms/`,
    // Dynamic field schema (V1.5) — enabled fields for registration forms
    FIELD_SCHEMA:       'visitors/field-schema/',
  },
  SMS: {
    MESSAGES:            'sms/messages/',
    MESSAGE_DETAIL:      (id: string) => `sms/messages/${id}/`,
    TEMPLATES:           'sms/templates/',
    TEMPLATE_DETAIL:     (id: string) => `sms/templates/${id}/`,
    CAMPAIGNS:           'sms/campaigns/',
    CAMPAIGN_DETAIL:     (id: string) => `sms/campaigns/${id}/`,
    CAMPAIGN_SEND:       (id: string) => `sms/campaigns/${id}/send/`,
    CAMPAIGN_CANCEL:     (id: string) => `sms/campaigns/${id}/cancel/`,
    CAMPAIGN_MESSAGES:   (id: string) => `sms/campaigns/${id}/messages/`,
    CAMPAIGN_ANALYTICS:  (id: string) => `sms/campaigns/${id}/analytics/`,
    CAMPAIGN_PREVIEW:    'sms/campaigns/preview/',
    SEND:                'sms/send/',
    CREDITS:             'sms/credits/',
    CREDIT_TOP_UP:       'sms/credits/top-up/',
    CREDIT_TRANSACTIONS: 'sms/credits/transactions/',
  },
  COMPLIANCE: {
    OVERVIEW:     'compliance/',
    EVENTS:       'compliance/events/',
    EVENT_DETAIL: (id: string) => `compliance/events/${id}/`,
    RULES:        'compliance/rules/',
    RULE_DETAIL:  (id: string) => `compliance/rules/${id}/`,
    // Backend URL: POST compliance/rules/create/
    RULE_CREATE:  'compliance/rules/create/',
    // Backend URL: DELETE compliance/rules/<id>/delete/
    RULE_DELETE:  (id: string) => `compliance/rules/${id}/delete/`,
  },
  ANALYTICS: {
    DASHBOARD: 'analytics/dashboard/',
    VISITORS:  'analytics/visitors/',
    SMS:       'analytics/sms/',
  },
  EXPORTS: {
    VISITORS: 'visitors/export/',
    LIST:     'analytics/exports/',
    CREATE:   'analytics/exports/',
    DETAIL:   (id: string) => `analytics/exports/${id}/`,
    DOWNLOAD: (id: string) => `analytics/exports/${id}/download/`,
  },
  DASHBOARD: {
    SUMMARY: 'dashboard/',
  },
  AUDIT: {
    LOGS:       'audit/logs/',
    LOG_DETAIL: (id: string) => `audit/logs/${id}/`,
  },
  SEARCH: {
    QUERY: 'search/',
  },
  PAYMENTS: {
    STATUS: (reference: string) => `payments/${reference}/status/`,
    RETRY:  (reference: string) => `payments/${reference}/retry/`,
  },
  VISITORS_BULK_IMPORT: 'visitors/bulk-import/',
  PLATFORM_ADMIN: {
    OVERVIEW:       'platform-admin/overview/',
    ORGANIZATIONS:  'platform-admin/organizations/',
    ORG_CREATE:     'platform-admin/organizations/create/',
    ORG_DETAIL:     (id: string) => `platform-admin/organizations/${id}/`,
    ORG_UPDATE:     (id: string) => `platform-admin/organizations/${id}/update/`,
    ORG_APPROVE:    (id: string) => `platform-admin/organizations/${id}/approve/`,
    ORG_REJECT:     (id: string) => `platform-admin/organizations/${id}/reject/`,
    ORG_SUSPEND:    (id: string) => `platform-admin/organizations/${id}/suspend/`,
    ORG_REINSTATE:  (id: string) => `platform-admin/organizations/${id}/reinstate/`,
    // Plan catalogue CRUD
    PLANS:         'platform-admin/plans/',
    PLAN_DETAIL:   (id: string) => `platform-admin/plans/${id}/`,
    // Plan change request management
    PLAN_REQUESTS:               'platform-admin/plan-requests/',
    PLAN_REQUEST_APPROVE: (id: string) => `platform-admin/plan-requests/${id}/approve/`,
    PLAN_REQUEST_REJECT:  (id: string) => `platform-admin/plan-requests/${id}/reject/`,
    // Credit top-up request management
    CREDIT_REQUESTS:               'platform-admin/credit-requests/',
    CREDIT_REQUEST_APPROVE: (id: string) => `platform-admin/credit-requests/${id}/approve/`,
    CREDIT_REQUEST_REJECT:  (id: string) => `platform-admin/credit-requests/${id}/reject/`,
    // SMS management (cross-tenant CRUD)
    SMS_TEMPLATES:                     'platform-admin/sms/templates/',
    SMS_TEMPLATE_DETAIL:  (id: string) => `platform-admin/sms/templates/${id}/`,
    SMS_MESSAGES:                      'platform-admin/sms/messages/',
    SMS_MESSAGE_DETAIL:   (id: string) => `platform-admin/sms/messages/${id}/`,
    SMS_CAMPAIGNS:                     'platform-admin/sms/campaigns/',
    SMS_CAMPAIGN_DETAIL:  (id: string) => `platform-admin/sms/campaigns/${id}/`,
    SMS_CREDITS:                       'platform-admin/sms/credits/',
    SMS_CREDIT_ADJUST:    (id: string) => `platform-admin/sms/credits/${id}/adjust/`,
    SMS_TRANSACTIONS:                  'platform-admin/sms/transactions/',
    // Auth management (cross-tenant)
    USERS:                             'platform-admin/auth/users/',
    USER_DETAIL:          (id: string) => `platform-admin/auth/users/${id}/`,
    MEMBERSHIPS:                       'platform-admin/auth/memberships/',
    MEMBERSHIP_DETAIL:    (id: string) => `platform-admin/auth/memberships/${id}/`,
    PASSWORD_RESET_TOKENS:             'platform-admin/auth/password-reset-tokens/',
    PASSWORD_RESET_TOKEN_REVOKE: (id: string) => `platform-admin/auth/password-reset-tokens/${id}/revoke/`,
    REFRESH_TOKENS:                    'platform-admin/auth/refresh-tokens/',
    REFRESH_TOKEN_REVOKE: (id: string) => `platform-admin/auth/refresh-tokens/${id}/revoke/`,
    // Kiosk management (cross-tenant)
    KIOSK_SESSIONS:                    'platform-admin/kiosk/sessions/',
    KIOSK_SESSION_DETAIL: (id: string) => `platform-admin/kiosk/sessions/${id}/`,
    // Plan / Credit request details
    PLAN_REQUEST_DETAIL:   (id: string) => `platform-admin/plan-requests/${id}/`,
    CREDIT_REQUEST_DETAIL: (id: string) => `platform-admin/credit-requests/${id}/`,
    // Invitations (cross-tenant)
    INVITATIONS:           'platform-admin/organizations/invitations/',
    INVITATION_DETAIL:     (id: string) => `platform-admin/organizations/invitations/${id}/`,
    // Organisation Settings (cross-tenant)
    ORG_SETTINGS:          'platform-admin/organizations/settings/',
    ORG_SETTINGS_DETAIL:   (id: string) => `platform-admin/organizations/settings/${id}/`,
    // Organisation Payments (read-only)
    ORG_PAYMENTS:          'platform-admin/payments/',
    ORG_PAYMENT_DETAIL:    (id: string) => `platform-admin/payments/${id}/`,
    // Subscriptions (cross-tenant)
    SUBSCRIPTIONS:                       'platform-admin/subscriptions/',
    SUBSCRIPTION_DETAIL:   (id: string) => `platform-admin/subscriptions/${id}/`,
    // Visitor Purposes (cross-tenant CRUD)
    VISITOR_PURPOSES_ADMIN:              'platform-admin/visitor-purposes/',
    VISITOR_PURPOSE_DETAIL: (id: string) => `platform-admin/visitor-purposes/${id}/`,
    // Visit Logs (cross-tenant, read-only)
    VISIT_LOGS:                          'platform-admin/visit-logs/',
    // Visitors (cross-tenant CRUD)
    VISITORS_ADMIN:                      'platform-admin/visitors/',
    VISITOR_ADMIN_DETAIL:  (id: string) => `platform-admin/visitors/${id}/`,
  },
  PLANS: {
    LIST:            'subscriptions/plans/',
    PLAN_REQUESTS:   'subscriptions/plan-requests/',
    PLAN_REQUEST_CONFIRM: (id: string) => `subscriptions/plan-requests/${id}/confirm-payment/`,
    TOP_UP_REQUESTS: 'subscriptions/credit-top-up-requests/',
    TOP_UP_CONFIRM:  (id: string) => `subscriptions/credit-top-up-requests/${id}/confirm-payment/`,
  },
  KIOSK: {
    SESSIONS:       'kiosk/sessions/',
    SESSION_DETAIL: (id: string) => `kiosk/sessions/${id}/`,
    SESSION_REGEN:  (id: string) => `kiosk/sessions/${id}/regenerate-token/`,
    CONFIG:         (token: string) => `kiosk/${token}/config/`,
    REGISTER:			(token: string) => `kiosk/${token}/register/`,
  },
  PRE_REGISTRATIONS: {
    LIST:    "pre-registrations/",
    CREATE:  "pre-registrations/",
    DETAIL:  (id: string) => `pre-registrations/${id}/`,
    UPDATE:  (id: string) => `pre-registrations/${id}/`,
    CANCEL:  (id: string) => `pre-registrations/${id}/cancel/`,
    QR:      (id: string) => `pre-registrations/${id}/qr/`,
    // Token-based (public + check-in)
    TOKEN_DETAIL:   (token: string) => `pre-register/${token}/`,
    TOKEN_COMPLETE: (token: string) => `pre-register/${token}/complete/`,
    TOKEN_LOOKUP:   (token: string) => `pre-register/${token}/lookup/`,
    TOKEN_CHECKIN:  (token: string) => `pre-register/${token}/checkin/`,
  },
} as const
